import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Building2, Mail, ArrowRight, Loader2, Sparkles, ShieldCheck, Check,
    AlertCircle, KeyRound, ExternalLink, ChevronLeft, Eye, EyeOff,
    Clock, MailCheck,
} from "lucide-react";
import TechBackground from "@/components/TechBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public tenant signup wizard (iter 61).
 *
 * Three-stop flow:
 *   1. Identify  — admin email + lab name + admin name → server checks for
 *      a same-email tenant; if found, we short-circuit into a "we already
 *      know you" splash. Otherwise we issue an email verification code.
 *   2. Verify    — 6-digit code from the email + the rest of the wizard
 *      data (subdomain slug + PDPPL consent). Submitting calls
 *      `POST /api/automaton/signup` atomically.
 *   3. Done      — show the new tenant's slug + temp credentials + a
 *      one-click "Go to login" CTA.
 */
export default function Signup() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [identity, setIdentity] = useState({
        company_name: "", admin_email: "", admin_name: "",
    });
    const [verify, setVerify] = useState({
        verification_id: "", code: "", slug: "", consent_pdppl: false,
    });
    const [tenantExists, setTenantExists] = useState(null);
    const [done, setDone] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Auto-suggest the slug from the company name in step 2.
    useEffect(() => {
        if (step !== 2 || verify.slug) return;
        const auto = identity.company_name.toLowerCase()
            .replace(/[^a-z0-9-]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 40);
        if (auto.length >= 3) setVerify((v) => ({ ...v, slug: auto }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, identity.company_name]);

    const startSignup = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!identity.admin_email || !identity.company_name || !identity.admin_name) {
            setError("All fields are required.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/automaton/signup-start`, identity);
            setVerify((v) => ({ ...v, verification_id: data.verification_id }));
            toast.success("We just emailed you a 6-digit verification code.");
            setStep(2);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            // Iter 61 — server returns structured 409 with code=tenant_exists
            // when the admin email already runs a Toothmatrix lab.
            if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "tenant_exists") {
                setTenantExists(detail);
            } else {
                setError((typeof detail === "string" ? detail : null) || "Couldn't start signup. Try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    const resendCode = async () => {
        if (!verify.verification_id) return;
        setBusy(true);
        try {
            await axios.post(`${API}/automaton/signup-resend`, { verification_id: verify.verification_id });
            toast.success("A fresh code is on its way.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed.");
        } finally {
            setBusy(false);
        }
    };

    const completeSignup = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!verify.code || verify.code.length !== 6) {
            setError("Please enter the 6-digit code from your email.");
            return;
        }
        if (!verify.slug || verify.slug.length < 3) {
            setError("Pick a subdomain — at least 3 chars, letters/digits/hyphens.");
            return;
        }
        if (!verify.consent_pdppl) {
            setError("Please accept the PDPPL acknowledgement to create your lab.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/automaton/signup`, {
                verification_id: verify.verification_id,
                code: verify.code,
                company_name: identity.company_name,
                slug: verify.slug,
                admin_email: identity.admin_email,
                admin_name: identity.admin_name,
                consent_pdppl: true,
                consent_pdppl_version: "1.0",
            });
            setDone(data);
            setStep(3);
        } catch (err) {
            setError(err?.response?.data?.detail || "Signup failed. Double-check the code and try again.");
        } finally {
            setBusy(false);
        }
    };

    // ─── Render branches ────────────────────────────────────────────────

    if (tenantExists) {
        return (
            <Shell>
                <KnownYouSplash
                    detail={tenantExists}
                    onTryAgain={() => { setTenantExists(null); setIdentity((i) => ({ ...i, admin_email: "" })); }}
                    onGoToLogin={() => navigate("/login")}
                />
            </Shell>
        );
    }

    if (step === 3 && done) {
        return (
            <Shell>
                <DoneSplash done={done} onGoToLogin={() => navigate("/login")} />
            </Shell>
        );
    }

    return (
        <Shell step={step}>
            {step === 1 ? (
                <IdentifyStep
                    identity={identity}
                    setIdentity={setIdentity}
                    onSubmit={startSignup}
                    busy={busy}
                    error={error}
                />
            ) : (
                <VerifyStep
                    identity={identity}
                    verify={verify}
                    setVerify={setVerify}
                    onSubmit={completeSignup}
                    onBack={() => setStep(1)}
                    onResend={resendCode}
                    busy={busy}
                    error={error}
                />
            )}
        </Shell>
    );
}

// ─── Layout shell ───────────────────────────────────────────────────────

function Shell({ children, step }) {
    // Iter 65 — split-screen layout matching /login: dark editorial visual on
    // the left, calm form column on the right. Uses the same TechBackground
    // and tr-* design tokens so signup feels like the natural sibling of sign-in.
    return (
        <div data-testid="signup-page" className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2 bg-white">
            {/* Visual side */}
            <div className="relative hidden lg:block bg-[#0B1120] tr-grain overflow-hidden">
                <TechBackground variant="dark" density={1.1} />
                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div />
                    <div>
                        <div className="tr-overline text-cyan-400 mb-3">· Toothmatrix · Lab onboarding</div>
                        <h1 className="font-display font-light text-5xl tracking-tight leading-[1.05]">
                            Run your lab on<br /><span className="italic text-cyan-300/90 font-extralight">a clinical-grade OS.</span>
                        </h1>
                        <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
                            Apply once. Our team reviews new lab applications within 24 hours. The moment you&rsquo;re approved, your 7-day free trial begins — every feature unlocked, no card needed.
                        </p>
                        <ul className="mt-7 space-y-2.5 text-[13.5px] text-white/75">
                            <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 text-cyan-300 shrink-0" /> Multi-tenant data isolation · PDPPL compliant</li>
                            <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 text-cyan-300 shrink-0" /> Cases · invoices · warranty · pricing quotes</li>
                            <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 text-cyan-300 shrink-0" /> Doctor commissions · purchase orders · finance model</li>
                        </ul>
                    </div>
                    <div className="flex items-center gap-2 text-white/50 font-mono-tr text-[11px] tracking-[0.2em] uppercase">
                        <ShieldCheck size={14} /> PDPPL · TLS · Hash-chained audit
                    </div>
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="tr-overline mb-3">· Toothmatrix · Start your free trial</div>
                    <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">
                        Apply for a lab.
                    </h1>
                    <p className="mt-2 text-slate-600 text-[14.5px]">
                        Tell us about your lab. We review every application personally — usually within 24 hours.
                    </p>
                    {step && step < 3 && (
                        <div className="mt-6"><StepIndicator step={step} /></div>
                    )}
                    <div className="mt-7">
                        {children}
                    </div>
                    <p className="mt-8 pt-6 border-t border-slate-100 text-[13px] text-slate-500">
                        Already have a lab? <Link to="/login" data-testid="signup-go-signin" className="text-cyan-700 font-medium hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

function StepIndicator({ step }) {
    return (
        <div className="flex items-center gap-3" data-testid="signup-step-indicator">
            <Dot active={step === 1} done={step > 1} label="Identify" />
            <div className="h-px flex-1 bg-slate-200 min-w-[20px]" />
            <Dot active={step === 2} done={step > 2} label="Verify" />
            <div className="h-px flex-1 bg-slate-200 min-w-[20px]" />
            <Dot active={false} done={step > 2} label="Submit" />
        </div>
    );
}

function Dot({ active, done, label }) {
    return (
        <div className="flex flex-col items-center gap-1.5 min-w-[58px]">
            <div className={`w-2.5 h-2.5 rounded-full transition-colors ${done ? "bg-emerald-500" : active ? "bg-cyan-600 ring-4 ring-cyan-100" : "bg-slate-300"}`} />
            <div className={`text-[10px] font-mono-tr tracking-[0.16em] uppercase ${active ? "text-cyan-700" : done ? "text-emerald-600" : "text-slate-400"}`}>{label}</div>
        </div>
    );
}

// ─── Step 1: Identify ───────────────────────────────────────────────────

function IdentifyStep({ identity, setIdentity, onSubmit, busy, error }) {
    return (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-step-identify">
            <Field label="Lab / company name" testid="signup-company">
                <input
                    type="text"
                    value={identity.company_name}
                    onChange={(e) => setIdentity({ ...identity, company_name: e.target.value })}
                    placeholder="Acme Dental Lab"
                    required minLength={2} maxLength={80}
                    autoFocus
                />
            </Field>
            <Field label="Your name" testid="signup-name">
                <input
                    type="text"
                    value={identity.admin_name}
                    onChange={(e) => setIdentity({ ...identity, admin_name: e.target.value })}
                    placeholder="Dr. Sara Khalifa"
                    required minLength={2} maxLength={80}
                />
            </Field>
            <Field label="Work email" testid="signup-email" hint="We'll send a 6-digit code to confirm it's really you.">
                <input
                    type="email"
                    value={identity.admin_email}
                    onChange={(e) => setIdentity({ ...identity, admin_email: e.target.value })}
                    placeholder="you@yourlab.com"
                    required
                />
            </Field>
            {error && <Alert msg={error} />}
            <button
                type="submit"
                disabled={busy}
                data-testid="signup-start-btn"
                className="tr-btn w-full !py-3 !text-[14px] disabled:opacity-60"
            >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                Send verification code
            </button>
        </form>
    );
}

// ─── Step 2: Verify + finalize ──────────────────────────────────────────

function VerifyStep({ identity, verify, setVerify, onSubmit, onBack, onResend, busy, error }) {
    const [slugStatus, setSlugStatus] = useState(null); // { available, reason }
    const [slugChecking, setSlugChecking] = useState(false);

    useEffect(() => {
        if (!verify.slug || verify.slug.length < 3) { setSlugStatus(null); return; }
        const t = setTimeout(async () => {
            setSlugChecking(true);
            try {
                const { data } = await axios.get(`${API}/automaton/slug-available`, { params: { slug: verify.slug } });
                setSlugStatus(data);
            } catch {
                setSlugStatus(null);
            } finally {
                setSlugChecking(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [verify.slug]);

    return (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-step-verify">
            <div className="-mt-2 mb-3 text-[12.5px] text-slate-500">
                Code sent to <strong className="font-mono-tr text-slate-700">{identity.admin_email}</strong>
                <button type="button" onClick={onBack} className="ml-2 text-cyan-700 hover:underline inline-flex items-center gap-0.5">
                    <ChevronLeft size={11} /> change
                </button>
            </div>
            <Field label="6-digit code" testid="signup-code">
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={verify.code}
                    onChange={(e) => setVerify({ ...verify, code: e.target.value.replace(/\D/g, "") })}
                    placeholder="000000"
                    required
                    className="!text-center !text-2xl !tracking-[10px] !font-mono-tr"
                />
                <button type="button" onClick={onResend} disabled={busy} className="text-[12px] text-cyan-700 hover:underline mt-1 inline-block disabled:text-slate-400" data-testid="signup-resend">
                    Resend code
                </button>
            </Field>
            <Field label="Choose your subdomain" testid="signup-slug" hint="Lower-case letters, digits, hyphens. 3–40 chars.">
                <div className="relative">
                    <input
                        type="text"
                        value={verify.slug}
                        onChange={(e) => setVerify({ ...verify, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                        placeholder="acme-dental"
                        required minLength={3} maxLength={40}
                        className="!pr-32"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11.5px] text-slate-400 font-mono-tr">.toothmatrix.com</span>
                </div>
                {verify.slug && verify.slug.length >= 3 && (
                    <div className="mt-1.5 text-[11.5px] flex items-center gap-1">
                        {slugChecking ? (
                            <><Loader2 size={11} className="animate-spin text-slate-400" /> <span className="text-slate-400">Checking…</span></>
                        ) : slugStatus?.available ? (
                            <><Check size={11} className="text-emerald-600" /> <span className="text-emerald-700">Available</span></>
                        ) : slugStatus ? (
                            <><AlertCircle size={11} className="text-rose-500" /> <span className="text-rose-600">{slugStatus.reason || "Taken"}</span></>
                        ) : null}
                    </div>
                )}
            </Field>
            <label className="flex items-start gap-2 text-[12.5px] text-slate-600 cursor-pointer pt-1" data-testid="signup-pdppl">
                <input
                    type="checkbox"
                    checked={verify.consent_pdppl}
                    onChange={(e) => setVerify({ ...verify, consent_pdppl: e.target.checked })}
                    className="mt-0.5"
                />
                <span>
                    I acknowledge the <a href="/compliance#pdppl" target="_blank" rel="noreferrer" className="text-cyan-700 hover:underline inline-flex items-center gap-0.5">Qatar PDPPL data-privacy policy <ExternalLink size={10} /></a> and confirm I'm authorised to set up this lab on Toothmatrix.
                </span>
            </label>
            {error && <Alert msg={error} />}
            <button
                type="submit"
                disabled={busy || (slugStatus && !slugStatus.available)}
                data-testid="signup-complete-btn"
                className="tr-btn w-full !py-3 !text-[14px] disabled:opacity-60"
            >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                Submit application for review
            </button>
        </form>
    );
}

// ─── Step 3: Done ───────────────────────────────────────────────────────

function DoneSplash({ done, onGoToLogin }) {
    // Iter 65 — application no longer auto-grants a trial. The splash now
    // confirms the application is in the review queue. No temp password
    // is issued until a platform superadmin approves the application.
    const appliedHuman = (() => {
        try { return new Date(done.applied_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
        catch { return done.applied_at; }
    })();
    return (
        <div data-testid="signup-done">
            <div className="mb-5">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
                    <MailCheck size={22} className="text-amber-700" />
                </div>
                <div className="tr-overline text-amber-700 mb-1.5">· Application submitted</div>
                <h2 className="font-display font-light text-3xl tracking-tight leading-tight text-slate-900">Thanks — we&rsquo;ve got it.</h2>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                    Your application for <strong>{done.company_name || done.tenant?.branding?.company_name}</strong> is now in our review queue. We look at every new lab personally — usually within <strong>24 hours</strong> — and we&rsquo;ll email your sign-in link the moment you&rsquo;re approved.
                </p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-3 text-[13px]">
                <Row k="Lab" v={done.company_name || done.tenant?.branding?.company_name} />
                <Row k="Email" v={done.superadmin_email} mono />
                <Row k="Subdomain" v={`${done.tenant?.slug}.toothmatrix.com`} mono />
                <Row k="Status" v="Pending review" />
                <Row k="Submitted" v={appliedHuman} />
            </div>

            <div className="mt-5 rounded-xl border border-cyan-100 bg-cyan-50/60 p-4">
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-800 mb-2 flex items-center gap-1.5">
                    <Clock size={11} /> What happens next
                </div>
                <ol className="space-y-1.5 text-[12.5px] text-slate-700 leading-relaxed list-decimal pl-4">
                    <li>Our reviewers verify your lab&rsquo;s details.</li>
                    <li>You receive an approval email with your sign-in link &amp; temporary password.</li>
                    <li>Your 7-day free trial begins on first sign-in.</li>
                </ol>
            </div>

            <p className="mt-5 text-[12px] text-slate-500">
                Didn&rsquo;t mean to apply, or need to chase this up? Email <a href="mailto:ceo@toothrocket.com" className="text-cyan-700 hover:underline">ceo@toothrocket.com</a>.
            </p>
            <button onClick={onGoToLogin} className="tr-btn w-full !py-3 mt-5 !text-[14px]" data-testid="signup-go-to-login">
                <ArrowRight size={15} /> Back to sign in
            </button>
        </div>
    );
}

// ─── "We already know you" splash ───────────────────────────────────────

function KnownYouSplash({ detail, onTryAgain, onGoToLogin }) {
    const t = detail.tenant;
    return (
        <div data-testid="signup-known-you-splash">
            <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cyan-100 mb-3">
                    <ShieldCheck size={22} className="text-cyan-700" />
                </div>
                <div className="font-mono-tr text-[10.5px] tracking-[0.3em] text-cyan-700 uppercase">· Already onboarded</div>
                <h2 className="font-display text-2xl tracking-tight text-slate-900 mt-1">We already know you.</h2>
                <p className="mt-1 text-[14px] text-slate-600">
                    <span className="font-mono-tr">{detail.admin?.email}</span> runs <strong>{t.company_name}</strong> on Toothmatrix. No need to sign up again.
                </p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-cyan-50 to-white border border-cyan-100 p-5 space-y-2 text-[13px]">
                <Row k="Lab" v={t.company_name} />
                <Row k="Subdomain" v={t.slug} mono />
                <Row k="Status" v={t.is_trialing ? "Trialing" : (t.status?.charAt(0).toUpperCase() + t.status?.slice(1))} />
            </div>
            <div className="flex items-center gap-2 mt-5">
                <button onClick={onTryAgain} className="tr-btn-ghost !py-2.5 flex-1" data-testid="signup-try-again">
                    <KeyRound size={13} /> Different email
                </button>
                <button onClick={onGoToLogin} className="tr-btn !py-2.5 flex-1" data-testid="signup-skip-to-login">
                    <ArrowRight size={13} /> Go to login
                </button>
            </div>
        </div>
    );
}

// ─── Primitives ─────────────────────────────────────────────────────────

function Field({ label, hint, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <div className="font-mono-tr text-[10.5px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">{label}</div>
            <div className="[&_input]:w-full [&_input]:px-3.5 [&_input]:py-2.5 [&_input]:rounded-lg [&_input]:border [&_input]:border-slate-200 [&_input]:focus:border-cyan-500 [&_input]:focus:ring-2 [&_input]:focus:ring-cyan-100 [&_input]:outline-none [&_input]:text-[14px]">
                {children}
            </div>
            {hint && <div className="mt-1 text-[11.5px] text-slate-500">{hint}</div>}
        </label>
    );
}

function Row({ k, v, mono, copyable, onCopy, secret }) {
    const [revealed, setRevealed] = useState(false);
    const display = secret && !revealed ? "•".repeat(Math.max(8, String(v ?? "").length)) : v;
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-slate-500 font-mono-tr text-[11px] uppercase tracking-[0.16em]">{k}</span>
            <div className="flex items-center gap-2 min-w-0 truncate">
                <span className={`text-slate-900 ${mono ? "font-mono-tr text-[12.5px]" : "text-[13px]"} truncate`} data-testid={secret ? "signup-temp-pw-value" : undefined}>{display}</span>
                {secret && (
                    <button
                        type="button"
                        onClick={() => setRevealed((r) => !r)}
                        className="text-slate-500 hover:text-slate-900 inline-flex items-center"
                        aria-label={revealed ? "Hide temporary password" : "Show temporary password"}
                        title={revealed ? "Hide" : "Show"}
                        data-testid="signup-toggle-pw"
                    >
                        {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                )}
                {copyable && (
                    <button type="button" onClick={onCopy} className="text-[11px] text-cyan-700 hover:underline" data-testid="signup-copy-pw">Copy</button>
                )}
            </div>
        </div>
    );
}

function Alert({ msg }) {
    return (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12.5px] text-rose-700 flex items-start gap-2" data-testid="signup-error">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{msg}</span>
        </div>
    );
}

// Eslint hush — used in JSX above
void Building2; void Mail;
